import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAccount, UserService } from '@shared/services/user.service';
import { Subject, takeUntil } from 'rxjs';
import { CrudService } from '../../../../shared/services/crud.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { PalletisingConfigurationViewModel } from '../palletising.configuration.view.model';
import { IBalance, IConfiguration, IConfigurationsResponse, IGetPalletTypesResponse, ILayerParam, IPalletType, IPalletTypeCombination, IPalletTypeItem, IPool, ISort, IStackParam, TBalanceMethod } from '../types';

@Injectable({
	providedIn: 'root',
})
export class PalletisingConfigurationService {
	public serverConfiguration: IConfiguration | null = null;

	private _accountCancel$ = new Subject<void>();

	constructor(
		private crudService: CrudService,
		private notificationService: NotificationService,
		private userService: UserService,
		public viewModel: PalletisingConfigurationViewModel,
	) {
		// we scope the handleError function, otherwise this will undefined
		this.handleError = this.handleError.bind(this);
		this.userService.accountChanged$.subscribe(() => {
			this._accountCancel$.next();
			this.resetCache();
		});
	}

	private resetCache(): void {
		this.serverConfiguration = null;
		this.viewModel.clear();
	}

	public loadConfiguration(account?: IAccount): void {
		this.viewModel.isLoadingConfiguration = true;
		this.crudService
			.get<IConfigurationsResponse>('/palletising/configurations', account)
			.pipe(takeUntil(this._accountCancel$))
			.subscribe({
				next: (value) => {
					if (value.items[0]) {
						this.serverConfiguration = value.items[0];
						this.setViewModelFromServerConfiguration();
						this.loadPalletTypes();
					}
				},
				error: this.handleError,
			});
	}

	private loadPalletTypes(): void {
		this.crudService
			.get<IGetPalletTypesResponse>('/palletising/pallettypes')
			.pipe(takeUntil(this._accountCancel$))
			.subscribe({
				next: this.setViewModelFromPalletTypes.bind(this),
				error: this.handleError,
			});
	}

	private setViewModelFromPalletTypes(value: IGetPalletTypesResponse): void {
		this.viewModel.palletTypes = this.getSortedClonedPalletTypes(value.items);
		this.viewModel.elementPalletTypes = this.getSortedClonedPalletTypes(value.items);
		this.viewModel.isLoadingConfiguration = false;
	}

	private getSortedClonedPalletTypes(value: IPalletTypeItem[]): IPalletTypeItem[] {
		const unsortedPalletTypes = value.map(({ description, name, self }) => {
			return {
				description,
				visible: true,
				name,
				selected: false,
				self,
			};
		});
		return unsortedPalletTypes.sort((a: IPalletTypeItem, b: IPalletTypeItem): -1 | 1 => (a.name < b.name ? -1 : 1));
	}

	private handleError(error: HttpErrorResponse) {
		this.viewModel.isUpdatingConfiguration = false;
		this.viewModel.isLoadingConfiguration = false;
		this.setViewModelFromServerConfiguration();
		if (error.status === 0) {
			this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
		} else {
			this.notificationService.notify(['masterdata.palletising.errors.unknown'], [], 'error');
		}
	}

	private setEnableDisablePriorityFormFields(): void {
		for (const { property, control } of this.viewModel.sortingFormFieldsLookup) {
			if (this.viewModel[property]) {
				control.enable();
			} else {
				control.reset();
				control.disable();
			}
		}
		for (const { property, control } of this.viewModel.poolingFormFieldsLookup) {
			if (this.viewModel[property]) {
				control.enable();
			} else {
				control.reset();
				control.disable();
			}
		}
	}

	public updatePriorityFormValidity(): void {
		/**
		 * Form.markAllAsTouched(); form.updateValueAndValidity();
		 *
		 * Will not trigger immediately, so we need to do this instead
		 */
		const sorting = Object.entries(this.viewModel.sortingPriorityForm.controls);
		for (const [_, control] of sorting) {
			control.markAsTouched({ onlySelf: true });
			control.updateValueAndValidity();
		}

		const pooling = Object.entries(this.viewModel.poolingPriorityForm.controls);
		for (const [_, control] of pooling) {
			control.markAsTouched({ onlySelf: true });
			control.updateValueAndValidity();
		}
	}

	public updateConfiguration(): void {
		this.setEnableDisablePriorityFormFields();
		this.updateOrderAndPriorityValues();
		this.updatePriorityFormValidity();
	}

	public saveConfiguration(): void {
		// saveConfiguration is only invoked by
		// the save button, but we guard anyways
		if (!this.viewModel.areFormsValid) {
			return;
		}
		this.viewModel.isUpdatingConfiguration = true;
		const updateConfiguration = this.getUpdateConfiguration();
		this.crudService.update<IConfiguration>(updateConfiguration).pipe((observable) => {
			observable.subscribe({
				next: (value) => {
					this.viewModel.isUpdatingConfiguration = false;
					// value is null (204) if there wasn't any changes, so check for this
					if (value) {
						this.serverConfiguration = value;
						this.notificationService.notify(['masterdata.palletising.configuration.update.success.heading'], ['masterdata.palletising.configuration.update.success.content'], 'success');
					}
				},
				error: this.handleError,
			});
			return observable;
		});
	}

	public onDeleteStackParam(param: IStackParam): void {
		this.viewModel.tableSource.data = this.viewModel.tableSource.data.filter((value) => value !== param);
		this.updateConfiguration();
	}

	public ondDeleteLayerParam(param: ILayerParam): void {
		this.viewModel.layersTableSource.data = this.viewModel.layersTableSource.data.filter((value) => value !== param);
		this.updateConfiguration();
	}

	public onCreateUpdateSave(stackParam: IStackParam): void {
		// pushing the stackParam doesn't trigger reactivity, so we reassign the data Array instead
		this.viewModel.tableSource.data = [...this.viewModel.tableSource.data, stackParam];
	}

	public onCreateUpdateLayerSave(layerParam: ILayerParam): void {
		// pushing the layerParam doesn't trigger reactivity, so we reassign the data Array instead
		this.viewModel.layersTableSource.data = [...this.viewModel.layersTableSource.data, layerParam];
	}

	private setViewModelFromServerConfiguration(): void {
		this.viewModel.allowStackingWithoutSupportingBase = this.serverConfiguration.allowStackingWithoutSupportingBase;
		this.viewModel.palletElementPadding = this.serverConfiguration.palletElementPadding;
		this.viewModel.palletMaxHeight = this.serverConfiguration.palletMaxHeight;
		this.viewModel.palletMaxLayers = this.serverConfiguration.palletMaxLayers;
		this.viewModel.palletMaxRows = this.serverConfiguration.palletMaxRows;
		this.viewModel.rotateMaxLengthToHeightAspectRatio = this.serverConfiguration.rotateMaxLengthToHeightAspectRatio;
		this.viewModel.rotateMaxWeight = this.serverConfiguration.rotateMaxWeight;
		this.viewModel.rotateMinRatioSingleElementOnly = this.serverConfiguration.rotateMinRatioSingleElementOnly;
		this.setViewModelSortingFromServerConfiguration();
		this.setViewModelPoolingFromServerConfiguration();
		this.setViewModelBalancingFromServerConfiguration();
		/** We don't want to set data to a direct reference to the serverConfiguration. So we create a deep cloned Array instead, so we don't mutate serverConfiguration. We only mutate the viewModel as a pattern and update the server with that data. */
		this.viewModel.tableSource.data = this.tableSourceData;
		this.viewModel.layersTableSource.data = this.layerTableSourceData;
		this.setEnableDisablePriorityFormFields();
	}

	private get layerTableSourceData(): ILayerParam[] {
		const params: ILayerParam[] = [];
		for (const param of this.serverConfiguration.stacking) {
			if (param.type.self === '/palletising/configuration-stacking-rule-types/layer') {
				if ('layer' in param) {
					params.push(param);
				}
			}
		}
		return structuredClone(params);
	}

	private get tableSourceData(): IStackParam[] {
		const params: IStackParam[] = [];
		for (const param of this.serverConfiguration.stacking) {
			if (param.type.self === '/palletising/configuration-stacking-rule-types/height') {
				if ('toHeight' in param) {
					params.push(param);
				}
			}
		}
		return structuredClone(params);
	}

	private getUpdateConfiguration(): IConfiguration {
		return {
			allowStackingWithoutSupportingBase: this.viewModel.allowStackingWithoutSupportingBase,
			balancing: this.getBalancingFromViewModel(),
			palletElementPadding: this.viewModel.palletElementPadding,
			palletMaxHeight: this.viewModel.palletMaxHeight,
			palletMaxLayers: this.viewModel.palletMaxLayers,
			palletMaxRows: this.viewModel.palletMaxRows,
			palletTypeCombinations: this.getPalletTypeCombinations(),
			pooling: this.getPoolingFromViewModel(),
			rotateMaxLengthToHeightAspectRatio: this.viewModel.rotateMaxLengthToHeightAspectRatio,
			rotateMaxWeight: this.viewModel.rotateMaxWeight,
			rotateMinRatioSingleElementOnly: this.viewModel.rotateMinRatioSingleElementOnly,
			self: this.serverConfiguration.self,
			sorting: this.getSortingFromViewModel(),
			stacking: this.getStackingFromViewModel(),
		};
	}

	private setViewModelBalancingFromServerConfiguration(): void {
		const balancing = this.serverConfiguration.balancing;
		if (balancing[0]) {
			const balance = balancing[0];
			if (balance.method === 0) {
				this.viewModel.balancingMethod = '0';
			} else if (balance.method === 1) {
				this.viewModel.balancingMethod = '1';
			} else if (balance.method === 2) {
				this.viewModel.balancingMethod = '2';
			} else {
				this.viewModel.balancingMethod = '-1';
			}
		} else {
			this.viewModel.balancingMethod = '-1';
		}
	}

	private setViewModelSortingFromServerConfiguration(): void {
		// set default values
		this.viewModel.sortingElementLength = false;
		this.viewModel.sortingElementLengthOrder = false;
		this.viewModel.sortingElementLengthPriority = null;
		this.viewModel.sortingPositionWeight = false;
		this.viewModel.sortingPositionWeightOrder = false;
		this.viewModel.sortingPositionWeightPriority = null;
		this.viewModel.sortingBatchNumber = false;
		this.viewModel.sortingBatchNumberOrder = false;
		this.viewModel.sortingBatchNumberPriority = null;
		this.viewModel.sortingProductionSequenceNumber = false;
		this.viewModel.sortingProductionSequenceNumberOrder = false;
		this.viewModel.sortingProductionSequenceNumberPriority = null;

		const sorting = this.serverConfiguration.sorting;
		for (const sort of sorting) {
			if (sort.type.self === '/palletising/configuration-sorting-strategies/elementLength') {
				this.viewModel.sortingElementLength = true;
				this.viewModel.sortingElementLengthOrder = sort.order === 0;
				this.viewModel.sortingElementLengthPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/palletising/configuration-sorting-strategies/positionWeight') {
				this.viewModel.sortingPositionWeight = true;
				this.viewModel.sortingPositionWeightOrder = sort.order === 0;
				this.viewModel.sortingPositionWeightPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/palletising/configuration-sorting-strategies/batchNumber') {
				this.viewModel.sortingBatchNumber = true;
				this.viewModel.sortingBatchNumberOrder = sort.order === 0;
				this.viewModel.sortingBatchNumberPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/palletising/configuration-sorting-strategies/productionSequenceNumber') {
				this.viewModel.sortingProductionSequenceNumber = true;
				this.viewModel.sortingProductionSequenceNumberOrder = sort.order === 0;
				this.viewModel.sortingProductionSequenceNumberPriority = sort.priority ? sort.priority : null;
			}
		}
	}

	private setViewModelPoolingFromServerConfiguration(): void {
		// set default values
		this.viewModel.poolingPallet = false;
		this.viewModel.poolingPalletUnspecified = false;
		this.viewModel.poolingPalletPriority = null;
		this.viewModel.poolingBatch = false;
		this.viewModel.poolingBatchUnspecified = false;
		this.viewModel.poolingBatchPriority = null;
		this.viewModel.poolingOrder = false;
		this.viewModel.poolingOrderUnspecified = false;
		this.viewModel.poolingOrderPriority = null;
		this.viewModel.poolingPartition = false;
		this.viewModel.poolingPartitionUnspecified = false;
		this.viewModel.poolingPartitionPriority = null;
		this.viewModel.poolingPalletDimension = false;
		this.viewModel.poolingPalletDimensionUnspecified = false;
		this.viewModel.poolingPalletDimensionPriority = null;
		this.viewModel.poolingPalletType = false;
		this.viewModel.poolingPalletTypeUnspecified = false;
		this.viewModel.poolingPalletTypePriority = null;
		this.viewModel.poolingGlassFitting = false;
		this.viewModel.poolingGlassFittingUnspecified = false;
		this.viewModel.poolingGlassFittingPriority = null;

		for (const pool of this.serverConfiguration.pooling) {
			if (pool.type.self === '/palletising/configuration-pooling-strategies/pallet') {
				this.viewModel.poolingPallet = true;
				this.viewModel.poolingPalletUnspecified = pool.isolateNullValues;
				this.viewModel.poolingPalletPriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/batch') {
				this.viewModel.poolingBatch = true;
				this.viewModel.poolingBatchUnspecified = pool.isolateNullValues;
				this.viewModel.poolingBatchPriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/order') {
				this.viewModel.poolingOrder = true;
				this.viewModel.poolingOrderUnspecified = pool.isolateNullValues;
				this.viewModel.poolingOrderPriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/partition') {
				this.viewModel.poolingPartition = true;
				this.viewModel.poolingPartitionUnspecified = pool.isolateNullValues;
				this.viewModel.poolingPartitionPriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/palletdimension') {
				this.viewModel.poolingPalletDimension = true;
				this.viewModel.poolingPalletDimensionUnspecified = pool.isolateNullValues;
				this.viewModel.poolingPalletDimensionPriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/pallettype') {
				this.viewModel.poolingPalletType = true;
				this.viewModel.poolingPalletTypeUnspecified = pool.isolateNullValues;
				this.viewModel.poolingPalletTypePriority = pool.priority ? pool.priority : null;
			}
			if (pool.type.self === '/palletising/configuration-pooling-strategies/glassfitting') {
				this.viewModel.poolingGlassFitting = true;
				this.viewModel.poolingGlassFittingUnspecified = pool.isolateNullValues;
				this.viewModel.poolingGlassFittingPriority = pool.priority ? pool.priority : null;
			}
		}
	}

	private getBalanceMethodFromViewModel(): TBalanceMethod {
		if (this.viewModel.balancingMethod === '0') {
			return 0;
		}
		if (this.viewModel.balancingMethod === '1') {
			return 1;
		}
		return 2;
	}

	private getBalancingFromViewModel(): IBalance[] {
		const balancing: IBalance[] = [];
		if (this.viewModel.balancingMethod !== '-1') {
			balancing.push({
				method: this.getBalanceMethodFromViewModel(),
				type: {
					self: '/palletising/configuration-balancing-strategies/weight',
				},
			});
		}
		return balancing;
	}

	private updateOrderAndPriorityValues(): void {
		if (!this.viewModel.sortingElementLength) {
			this.viewModel.sortingElementLengthOrder = false;
			this.viewModel.sortingElementLengthPriority = null;
		}
		if (!this.viewModel.sortingPositionWeight) {
			this.viewModel.sortingPositionWeightOrder = false;
			this.viewModel.sortingPositionWeightPriority = null;
		}
		if (!this.viewModel.sortingBatchNumber) {
			this.viewModel.sortingBatchNumberOrder = false;
			this.viewModel.sortingBatchNumberPriority = null;
		}
		if (!this.viewModel.sortingProductionSequenceNumber) {
			this.viewModel.sortingProductionSequenceNumberOrder = false;
			this.viewModel.sortingProductionSequenceNumberPriority = null;
		}
		if (!this.viewModel.poolingPallet) {
			this.viewModel.poolingPalletUnspecified = false;
			this.viewModel.poolingPalletPriority = null;
		}
		if (!this.viewModel.poolingBatch) {
			this.viewModel.poolingBatchUnspecified = false;
			this.viewModel.poolingBatchPriority = null;
		}
		if (!this.viewModel.poolingOrder) {
			this.viewModel.poolingOrderUnspecified = false;
			this.viewModel.poolingOrderPriority = null;
		}
		if (!this.viewModel.poolingPartition) {
			this.viewModel.poolingPartitionUnspecified = false;
			this.viewModel.poolingPartitionPriority = null;
		}
		if (!this.viewModel.poolingPalletDimension) {
			this.viewModel.poolingPalletDimensionUnspecified = false;
			this.viewModel.poolingPalletDimensionPriority = null;
		}
		if (!this.viewModel.poolingPalletType) {
			this.viewModel.poolingPalletTypeUnspecified = false;
			this.viewModel.poolingPalletTypePriority = null;
		}
		if (!this.viewModel.poolingGlassFitting) {
			this.viewModel.poolingGlassFittingUnspecified = false;
			this.viewModel.poolingGlassFittingPriority = null;
		}
	}

	private getStackingFromViewModel(): (IStackParam | ILayerParam)[] {
		return [...this.viewModel.tableSource.data, ...this.viewModel.layersTableSource.data];
	}

	private getSortingFromViewModel(): ISort[] {
		const sorting: ISort[] = [];
		if (this.viewModel.sortingElementLength) {
			const priority = this.viewModel.sortingElementLengthPriority;
			sorting.push({
				order: this.viewModel.sortingElementLengthOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-sorting-strategies/elementLength',
				},
			});
		}
		if (this.viewModel.sortingPositionWeight) {
			const priority = this.viewModel.sortingPositionWeightPriority;
			sorting.push({
				order: this.viewModel.sortingPositionWeightOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-sorting-strategies/positionWeight',
				},
			});
		}
		if (this.viewModel.sortingBatchNumber) {
			const priority = this.viewModel.sortingBatchNumberPriority;
			sorting.push({
				order: this.viewModel.sortingBatchNumberOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-sorting-strategies/batchNumber',
				},
			});
		}
		if (this.viewModel.sortingProductionSequenceNumber) {
			const priority = this.viewModel.sortingProductionSequenceNumberPriority;
			sorting.push({
				order: this.viewModel.sortingProductionSequenceNumberOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-sorting-strategies/productionSequenceNumber',
				},
			});
		}
		return sorting;
	}

	private getPoolingFromViewModel(): IPool[] {
		const pooling: IPool[] = [];
		if (this.viewModel.poolingPallet) {
			const priority = this.viewModel.poolingPalletPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingPalletUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/pallet',
				},
			});
		}
		if (this.viewModel.poolingBatch) {
			const priority = this.viewModel.poolingBatchPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingBatchUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/batch',
				},
			});
		}
		if (this.viewModel.poolingOrder) {
			const priority = this.viewModel.poolingOrderPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingOrderUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/order',
				},
			});
		}
		if (this.viewModel.poolingPartition) {
			const priority = this.viewModel.poolingPartitionPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingPartitionUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/partition',
				},
			});
		}
		if (this.viewModel.poolingPalletDimension) {
			const priority = this.viewModel.poolingPalletDimensionPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingPalletDimensionUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/palletdimension',
				},
			});
		}
		if (this.viewModel.poolingPalletType) {
			const priority = this.viewModel.poolingPalletTypePriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingPalletTypeUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/pallettype',
				},
			});
		}
		if (this.viewModel.poolingGlassFitting) {
			const priority = this.viewModel.poolingGlassFittingPriority;
			pooling.push({
				isolateNullValues: this.viewModel.poolingGlassFittingUnspecified,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/palletising/configuration-pooling-strategies/glassfitting',
				},
			});
		}
		return pooling;
	}

	public palletTypeSelected(item: IPalletTypeItem | null): void {
		this.viewModel.selectedPalletType = item;
		this.updateAssociatedTypes();
	}

	private updateAssociatedTypes(): void {
		this.deSelectAllAssociatedTypes();
		this.selectAssociatedTypesFromServerConfiguration();
		void this.filterAssociatedTypes();
	}

	private async filterAssociatedTypes(): Promise<void> {
		this.viewModel.filteredAssociatedTypes = [];

		// we have to let Angular do a render pass here,
		// or we get ExpressionChangedAfterItHasBeenCheckedError
		await this.delay();

		if (this.viewModel.selectedPalletType) {
			const associated = this.viewModel.elementPalletTypes;
			this.viewModel.filteredAssociatedTypes = associated.filter((element) => {
				return element.self !== this.viewModel.selectedPalletType.self;
			});
			this.viewModel.filteredAssociatedTypes.sort((a: IPalletTypeItem, b: IPalletTypeItem): -1 | 1 => (a.name < b.name ? -1 : 1));
		} else {
			this.viewModel.filteredAssociatedTypes = [];
		}
	}

	// delay function, so Angular can do a render pass and not throw a
	// ExpressionChangedAfterItHasBeenCheckedError because the binding
	// values are changing too fast and Angular doesn't detect it
	private async delay(): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, 0));
	}

	private selectAssociatedTypesFromServerConfiguration(): void {
		const selectedCombination = this.getSelectedCombinationFromServerConfiguration();
		if (selectedCombination) {
			for (const element of selectedCombination.elementPalletTypes) {
				this.setAssociatedTypeSelectedValue(element, true);
			}
		}
	}

	private setAssociatedTypeSelectedValue(elementType: IPalletType, value: boolean): void {
		for (const element of this.viewModel.elementPalletTypes) {
			if (element.self === elementType.palletType.self) {
				element.selected = value;
			}
		}
	}

	private deSelectAllAssociatedTypes(): void {
		for (const element of this.viewModel.elementPalletTypes) {
			element.selected = false;
		}
	}

	private getSelectedCombinationFromServerConfiguration(): IPalletTypeCombination | null {
		if (this.serverConfiguration) {
			const selected = this.viewModel.selectedPalletType;
			if (selected) {
				for (const combination of this.serverConfiguration.palletTypeCombinations) {
					if (selected.self === combination.palletType.self) {
						return combination;
					}
				}
			}
		}
		return null;
	}

	private getPalletTypeCombinations(): IPalletTypeCombination[] {
		const selected = this.viewModel.selectedPalletType;
		const combinations: IPalletTypeCombination[] = [];
		const existing = this.getSelectedCombinationFromServerConfiguration();
		if (existing) {
			existing.elementPalletTypes = this.getSelectedElementTypes();
			combinations.push(existing);
		} else if (selected) {
			combinations.push({
				palletType: {
					self: selected.self,
				},
				elementPalletTypes: this.getSelectedElementTypes(),
			});
		}
		combinations.push(...this.getNonSelectedCombinationsFromConfiguration());
		return combinations;
	}

	private getNonSelectedCombinationsFromConfiguration(): IPalletTypeCombination[] {
		if (this.viewModel.selectedPalletType && this.serverConfiguration) {
			return this.serverConfiguration.palletTypeCombinations.filter((combination) => {
				return combination.palletType.self !== this.viewModel.selectedPalletType.self;
			});
		}
		return [];
	}

	private getSelectedElementTypes(): IPalletType[] {
		return this.viewModel.elementPalletTypes
			.filter((item) => item.selected)
			.map(({ self }): IPalletType => {
				return {
					palletType: {
						self,
					},
				};
			});
	}
}
